import type { Settings } from '@ais-ui/websockets';
import type {
  DomainTranslator,
  LayerOrder,
  MapLayerOrdering,
  MapSettings,
} from '../types';

const MAPENGINE_KEY = 'assetDisplay.mapengine';

const DEFAULT_LAYER_ORDERING: MapLayerOrdering = { version: 1, layers: [] };

const DEFAULTS: MapSettings = {
  viewport: { center: { lat: 56, lon: 11 }, zoom: 6 },
  base_maps: { osm_enabled: true, s57_enabled: false, osm_base_url: '' },
  overlays: {
    graticule: false,
    scale: false,
    cursor_position: false,
    cursor_position_format: 'DD',
    zoom_buttons: true,
  },
  layer_ordering: DEFAULT_LAYER_ORDERING,
};

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' ? (value as Record<string, unknown>) : {};

const pick = <V>(value: unknown, fallback: V): V =>
  value === undefined || value === null ? fallback : (value as V);

/**
 * Translator for the `map` domain. Maps the legacy blob's
 * `assetDisplay.mapengine.*` (viewport + base maps), `ribbonPlugin.settings`
 * overlay flags, and `ribbonPlugin.settings.layer_ordering` to/from the clean,
 * backend-aligned `MapSettings` shape.
 */
export const mapTranslator: DomainTranslator<MapSettings> = {
  fromBlob(blob: Settings): MapSettings {
    const engine = asRecord(
      (blob as unknown as Record<string, unknown>)[MAPENGINE_KEY],
    );
    const view = asRecord(engine.viewParam);
    const center = asRecord(view.center);
    const ribbon = asRecord(blob['ribbonPlugin.settings']);

    return {
      viewport: {
        center: {
          lat: pick(center.lat, DEFAULTS.viewport.center.lat),
          lon: pick(center.lon, DEFAULTS.viewport.center.lon),
        },
        // Blob stores `zoomLevel`; the backend/domain shape uses `zoom`.
        zoom: pick(view.zoomLevel, DEFAULTS.viewport.zoom),
      },
      base_maps: {
        osm_enabled: pick(engine.fEnableOSM, DEFAULTS.base_maps.osm_enabled),
        s57_enabled: pick(engine.fEnableS57, DEFAULTS.base_maps.s57_enabled),
        osm_base_url: pick(engine.osmBaseUrl, DEFAULTS.base_maps.osm_base_url),
      },
      overlays: {
        graticule: pick(ribbon.graticule_on, DEFAULTS.overlays.graticule),
        scale: pick(ribbon.scale_on, DEFAULTS.overlays.scale),
        cursor_position: DEFAULTS.overlays.cursor_position,
        cursor_position_format: DEFAULTS.overlays.cursor_position_format,
        zoom_buttons: DEFAULTS.overlays.zoom_buttons,
      },
      layer_ordering: {
        version: pick(
          asRecord(ribbon.layer_ordering).version,
          DEFAULT_LAYER_ORDERING.version,
        ),
        layers: pick(
          asRecord(ribbon.layer_ordering).layers as LayerOrder[] | undefined,
          DEFAULT_LAYER_ORDERING.layers,
        ),
      },
    };
  },

  applyToBlob(blob: Settings, patch: Partial<MapSettings>): Settings {
    const next = { ...(blob as unknown as Record<string, unknown>) };
    const engine = { ...asRecord(next[MAPENGINE_KEY]) };
    const ribbon = { ...asRecord(next['ribbonPlugin.settings']) };

    if (patch.viewport) {
      const prevView = asRecord(engine.viewParam);
      const prevCenter = asRecord(prevView.center);
      engine.viewParam = {
        ...prevView,
        center: {
          ...prevCenter,
          ...(patch.viewport.center?.lat !== undefined && {
            lat: patch.viewport.center.lat,
          }),
          ...(patch.viewport.center?.lon !== undefined && {
            lon: patch.viewport.center.lon,
          }),
        },
        ...(patch.viewport.zoom !== undefined && {
          zoomLevel: patch.viewport.zoom,
        }),
      };
    }

    if (patch.base_maps) {
      if (patch.base_maps.osm_enabled !== undefined)
        engine.fEnableOSM = patch.base_maps.osm_enabled;
      if (patch.base_maps.s57_enabled !== undefined)
        engine.fEnableS57 = patch.base_maps.s57_enabled;
      if (patch.base_maps.osm_base_url !== undefined)
        engine.osmBaseUrl = patch.base_maps.osm_base_url;
    }

    if (patch.overlays) {
      if (patch.overlays.graticule !== undefined)
        ribbon.graticule_on = patch.overlays.graticule;
      if (patch.overlays.scale !== undefined)
        ribbon.scale_on = patch.overlays.scale;
    }

    if (patch.layer_ordering) {
      ribbon.layer_ordering = {
        version: patch.layer_ordering.version,
        layers: patch.layer_ordering.layers,
      };
    }

    next[MAPENGINE_KEY] = engine;
    next['ribbonPlugin.settings'] = ribbon;
    return next as unknown as Settings;
  },
};
